import {useAlive} from '@github-ui/use-alive'
import {useQuery} from '@github-ui/react-query'
import useIsMounted from '@github-ui/use-is-mounted'
import {useMemo} from 'react'

export interface AgentSessionAlivePayload {
  pull_request_id: number
  owner_id: number
  repo_id: number
  session_id: string
  state: string
  pull_request_global_id: string
  task_id: string
}

export interface UseAgentSessionsAliveOptions {
  enableCreateEvents?: boolean
  enableUpdateEvents?: boolean
  enableLogUpdates?: boolean
  enableTaskUpdates?: boolean
  enabled?: boolean
  onEvent: (payload?: AgentSessionAlivePayload) => void
}

export interface UseAgentSessionsAliveResult {
  /** Whether Alive channels were successfully fetched and subscriptions are active */
  isConnected: boolean
  /** Whether the channels query is currently loading */
  isLoading: boolean
  /** Whether the channels query has failed */
  isError: boolean
}

/**
 * Hook that subscribes to agent session alive updates and triggers callback when events are received.
 *
 * @param options.onEvent - Callback triggered when session events occur
 * @param options.enableCreateEvents - Whether to subscribe to create events (default: true)
 * @param options.enableUpdateEvents - Whether to subscribe to update events (default: true)
 * @param options.enableLogUpdates - Whether to subscribe to log_update events (default: false)
 * @param options.enableTaskUpdates - Whether to subscribe to task_update events (default: false)
 * @param options.enabled - Whether the hook is enabled (default: true)
 * @returns Object with connection status: isConnected, isLoading, isError
 */
export function useAgentSessionsAlive(options: UseAgentSessionsAliveOptions): UseAgentSessionsAliveResult {
  const {
    onEvent,
    enableCreateEvents = true,
    enableUpdateEvents = true,
    enableLogUpdates = false,
    enableTaskUpdates = false,
    enabled,
  } = options
  const isMounted = useIsMounted()

  const handleAliveEvent = useMemo(
    () => (payload?: AgentSessionAlivePayload) => {
      if (isMounted()) {
        onEvent(payload)
      }
    },
    [isMounted, onEvent],
  )

  const {
    data: channels,
    isLoading,
    isError,
    isSuccess,
  } = useQuery({
    queryKey: ['agent-session-alive-channels'],
    queryFn: async () => {
      const response = await fetch('https://github.githubassets.com/copilot/agent-sessions/alive-channels', {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch Alive channels')
      }

      const result = await response.json()

      return {
        viewerCopilotAgentCreatesChannel: result.creates_channel,
        viewerCopilotAgentUpdatesChannel: result.updates_channel,
        viewerCopilotAgentLogUpdatesChannel: result.log_updates_channel,
        viewerCopilotAgentTaskUpdatesChannel: result.task_updates_channel,
      }
    },
    staleTime: Infinity,
    enabled: enabled ?? true,
  })

  // When disabled, channels will be undefined and useAlive will handle it gracefully
  useAlive(enableCreateEvents ? channels?.viewerCopilotAgentCreatesChannel : undefined, handleAliveEvent)
  useAlive(enableUpdateEvents ? channels?.viewerCopilotAgentUpdatesChannel : undefined, handleAliveEvent)
  useAlive(enableLogUpdates ? channels?.viewerCopilotAgentLogUpdatesChannel : undefined, handleAliveEvent)
  useAlive(enableTaskUpdates ? channels?.viewerCopilotAgentTaskUpdatesChannel : undefined, handleAliveEvent)

  // isConnected is true when channels have been successfully fetched and at least one channel exists
  const isConnected =
    isSuccess &&
    !!(
      channels?.viewerCopilotAgentCreatesChannel ||
      channels?.viewerCopilotAgentUpdatesChannel ||
      channels?.viewerCopilotAgentLogUpdatesChannel ||
      channels?.viewerCopilotAgentTaskUpdatesChannel
    )

  return {isConnected, isLoading, isError}
}
